import { UseFormSetValue } from 'react-hook-form';
import {
  lastQuarterEnd,
  lastQuarterStart,
  getLastWeekStart,
  getLastWeekEnd,
  getLastYearStart,
  getLastYearEnd,
  getLastMonthStart,
} from 'wisestep-base';

export enum SmartDateOptions {
  TODAY = 'Today',
  YESTERDAY = 'Yesterday',
  LAST_TWENTY_FOUR_HOURS = 'Last 24 Hours',
  WEEK = '7D',
  THIS_WEEK = 'This Week',
  LAST_WEEK = 'Last Week',
  LAST_SEVEN_DAYS = 'Last 7 Days',
  MONTH = '30D',
  THIS_MONTH = 'This Month',
  LAST_MONTH = 'Last Month',
  LAST_THIRTY_DAYS = 'Last 30 Days',
  MTD = 'MTD',
  QTD = 'QTD',
  QUARTERLY = '90D',
  THIS_QUARTER = 'This Quarter',
  LAST_QUARTER = 'Last Quarter',
  SIX_MONTH = '180D',
  YEAR = '1Year',
  YTD = 'YTD',
  THIS_YEAR = 'This Year',
  LAST_YEAR = 'Last Year',
}

export const getEnumFromLabel = (value: string): string | undefined => {
  const entry = Object.entries(SmartDateOptions).find(([, v]) => v === value);
  return entry ? entry[0] : undefined;
};

export const smartDateOptionsArray = [
  SmartDateOptions.TODAY,
  SmartDateOptions.YESTERDAY,
  SmartDateOptions.WEEK,
  SmartDateOptions.MONTH,
  SmartDateOptions.MTD,
];

export const smartDateOptionsArrayForPipelineReport = [
  SmartDateOptions.YEAR,
  SmartDateOptions.SIX_MONTH,
  SmartDateOptions.QUARTERLY,
  SmartDateOptions.MONTH,
  SmartDateOptions.MTD,
  SmartDateOptions.QTD,
];

export const smartDateOptionsArrayForComposable = [
  SmartDateOptions.TODAY,
  SmartDateOptions.YESTERDAY,
  SmartDateOptions.THIS_WEEK,
  SmartDateOptions.THIS_MONTH,
  SmartDateOptions.THIS_QUARTER,
  SmartDateOptions.THIS_YEAR,
];

export const smartDateFiltersForAmAnalytics = [
  SmartDateOptions.TODAY,
  SmartDateOptions.YESTERDAY,
  SmartDateOptions.THIS_WEEK,
  SmartDateOptions.LAST_WEEK,
  SmartDateOptions.THIS_MONTH,
  SmartDateOptions.LAST_MONTH,
  SmartDateOptions.THIS_QUARTER,
  SmartDateOptions.LAST_QUARTER,
];

export const allSmartDateOptionsArrayForComposable = [
  SmartDateOptions.TODAY,
  SmartDateOptions.YESTERDAY,
  SmartDateOptions.LAST_TWENTY_FOUR_HOURS,
  SmartDateOptions.THIS_WEEK,
  SmartDateOptions.LAST_WEEK,
  SmartDateOptions.LAST_SEVEN_DAYS,
  SmartDateOptions.THIS_MONTH,
  SmartDateOptions.LAST_MONTH,
  SmartDateOptions.THIS_QUARTER,
  SmartDateOptions.LAST_QUARTER,
  SmartDateOptions.THIS_YEAR,
  SmartDateOptions.LAST_YEAR,
];

export const onSmartDateSelect = (setValue: UseFormSetValue<any>) => {
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const toDate = new Date(new Date().setHours(23, 59, 59, 999));
  const setDateValue = (fromDate) => {
    setValue('fromDate', fromDate);
    setValue('toDate', toDate);
  };
  return {
    [SmartDateOptions.TODAY]: () => setDateValue(today),

    [SmartDateOptions.YESTERDAY]: () => {
      const fromDate = new Date(today.setDate(today.getDate() - 1));
      setValue('fromDate', fromDate);
      setValue('toDate', new Date(fromDate.setHours(23, 59, 59)));
    },
    [SmartDateOptions.LAST_TWENTY_FOUR_HOURS]: () => {
      const from = new Date();
      from.setHours(from.getHours() - 24);
      setDateValue(from);
    },
    [SmartDateOptions.WEEK]: () => {
      const fromDate = new Date(today.setDate(today.getDate() - 7));
      setDateValue(fromDate);
    },
    [SmartDateOptions.LAST_SEVEN_DAYS]: () => {
      const from = new Date();
      from.setHours(from.getHours() - 7 * 24);
      setDateValue(from);
    },
    [SmartDateOptions.THIS_WEEK]: () => {
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      today.setDate(today.getDate() - diffToMonday);
      setDateValue(today);
    },
    [SmartDateOptions.LAST_WEEK]: () => {
      setValue('fromDate', getLastWeekStart.add(1, 'day'));
      setValue('toDate', getLastWeekEnd.add(1, 'day'));
    },
    [SmartDateOptions.MONTH]: () => {
      const fromDate = new Date(today.setDate(today.getDate() - 30));
      setDateValue(fromDate);
    },
    [SmartDateOptions.THIS_MONTH]: () => {
      const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateValue(fromDate);
    },
    [SmartDateOptions.MTD]: () => {
      const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateValue(fromDate);
    },
    [SmartDateOptions.LAST_MONTH]: () => {
      const toDate = new Date();
      toDate.setDate(1);
      toDate.setHours(23, 59, 59);
      toDate.setHours(toDate.getHours() - 24);
      setValue('fromDate', getLastMonthStart);
      setValue('toDate', toDate);
    },
    [SmartDateOptions.SIX_MONTH]: () => {
      const fromDate = new Date(today.setMonth(today.getMonth() - 6));
      setDateValue(fromDate);
    },
    [SmartDateOptions.LAST_QUARTER]: () => {
      setValue('fromDate', lastQuarterStart);
      setValue('toDate', lastQuarterEnd);
    },
    [SmartDateOptions.QUARTERLY]: () => {
      const fromDate = new Date(today.setDate(today.getDate() - 90));
      setDateValue(fromDate);
    },
    [SmartDateOptions.YEAR]: () => {
      const fromDate = new Date(today.setDate(today.getDate() - 365));
      setDateValue(fromDate);
    },
    [SmartDateOptions.QTD]: () => {
      const now = new Date();
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const fromDate = new Date(now.getFullYear(), quarterStartMonth, 1);
      setDateValue(fromDate);
    },
    [SmartDateOptions.THIS_QUARTER]: () => {
      const now = new Date();
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const fromDate = new Date(now.getFullYear(), quarterStartMonth, 1);
      setDateValue(fromDate);
    },
    [SmartDateOptions.YTD]: () => {
      const fromDate = new Date(new Date().getFullYear(), 0, 1);
      setDateValue(fromDate);
    },
    [SmartDateOptions.THIS_YEAR]: () => {
      const fromDate = new Date(new Date().getFullYear(), 0, 1);
      setDateValue(fromDate);
    },
    [SmartDateOptions.LAST_YEAR]: () => {
      setValue('fromDate', getLastYearStart);
      setValue('toDate', getLastYearEnd);
    },
  };
};
